import type { TrackerAdapter } from '../types';
import {
  PAGE_STAY_EVENT_NAME,
  MANUAL_STAY_EVENT_NAME,
  DEFAULT_PAGE_STAY_MIN_DURATION,
} from '../constants';
import { isBrowser, getPageUrl } from '../utils';

// ---------------------------------------------------------------------------
// StayTimer — 手动计时器工具
// ---------------------------------------------------------------------------

interface StayTimerEntry {
  name: string;
  startTime: number;
}

export class StayTimer {
  private _timers: Map<string, StayTimerEntry> = new Map();

  /**
   * 开始计时
   * 如果同名计时器已存在，会先移除旧的（避免内存泄漏）
   */
  start(name: string): void {
    if (!isBrowser()) return;

    // 同名计时器已存在时，先移除旧的
    if (this._timers.has(name)) {
      console.warn(`[yl-nsm-track] StayTimer "${name}" already exists. Restarting.`);
    }

    this._timers.set(name, { name, startTime: performance.now() });
  }

  /**
   * 结束计时并返回时长（ms）
   * 如果找不到对应计时器，返回 null
   */
  end(name: string): { durationMs: number } | null {
    if (!isBrowser()) return null;

    const entry = this._timers.get(name);
    if (!entry) {
      console.warn(`[yl-nsm-track] StayTimer "${name}" not found. Did you forget to call start()?`);
      return null;
    }

    this._timers.delete(name);
    const durationMs = Math.round(performance.now() - entry.startTime);
    return { durationMs };
  }

  /**
   * 获取当前正在计时的名称列表
   */
  getActiveTimers(): string[] {
    return Array.from(this._timers.keys());
  }

  /**
   * 清理所有未结束的计时器
   */
  clear(): void {
    this._timers.clear();
  }
}

// ---------------------------------------------------------------------------
// 自动页面停留采集
// ---------------------------------------------------------------------------

interface PageStayObserverOptions {
  /** 最小上报阈值（ms） */
  minDuration?: number;
  /** 是否开启调试日志 */
  debug?: boolean;
}

interface PageStayObserver {
  /**
   * 上报当前页面的停留时长并重置计时。
   * 由 core.ts 在 pv() 方法中调用，用于 SPA 路由切换场景。
   */
  reportCurrent(): void;
}

/**
 * 设置页面停留时长自动采集
 *
 * 工作流程：
 * 1. init() 时记录 pageEnterTime
 * 2. 注册 visibilitychange / pagehide / beforeunload 事件
 * 3. 页面进入 hidden / 卸载时，计算并上报停留时长
 * 4. 返回 { reportCurrent } 供 SPA 路由切换时调用
 *
 * 预渲染保护：只在 visibilityState === 'visible' 时开始计时
 */
export function setupPageStayObserver(
  adapter: TrackerAdapter,
  options?: PageStayObserverOptions,
): PageStayObserver {
  const minDuration = options?.minDuration ?? DEFAULT_PAGE_STAY_MIN_DURATION;
  const debug = options?.debug === true;

  if (!isBrowser()) {
    return { reportCurrent: () => {} };
  }

  const logDebug = (message: string, data?: Record<string, any>) => {
    if (!debug) return;
    if (data) {
      console.info(`[yl-nsm-track] ${message}`, data);
    } else {
      console.info(`[yl-nsm-track] ${message}`);
    }
  };

  // 当前页面进入时间（仅在页面可见时记录）
  let pageEnterTime: number | null = null;
  // 标记是否已初始化（避免重复注册事件）
  let initialized = false;
  // 当前页面 URL（用于上报时区分页面）
  let currentPageUrl: string = '';

  /**
   * 计算并上报停留时长
   */
  const reportStay = (reason: string): void => {
    if (pageEnterTime === null) {
      logDebug('No page enter time recorded. Skipping stay report.', { reason });
      return;
    }

    const durationMs = Math.round(performance.now() - pageEnterTime);

    // 重置计时（无论是否上报，都清空）
    pageEnterTime = null;

    // 阈值过滤
    if (durationMs < minDuration) {
      logDebug(`Stay duration ${durationMs}ms below min threshold ${minDuration}ms. Skipping.`, {
        reason,
      });
      return;
    }

    const props: Record<string, any> = {
      ...adapter.getDefaultProps(),
      duration_ms: durationMs,
      duration_seconds: Math.round(durationMs / 1000),
    };

    // 如果当前页面 URL 有记录，覆盖默认的 page_url（避免 SPA 切换后取到新 URL）
    if (currentPageUrl) {
      props.page_url = currentPageUrl;
    }

    adapter.event(PAGE_STAY_EVENT_NAME, props);

    logDebug(`Page stay reported: ${durationMs}ms`, { reason, page_url: props.page_url });
  };

  /**
   * 记录页面进入时间（仅在页面可见时）
   */
  const recordEnterTime = () => {
    if (document.visibilityState === 'visible') {
      pageEnterTime = performance.now();
      currentPageUrl = getPageUrl();
      logDebug('Page enter time recorded.', { time: pageEnterTime });
    } else {
      // 页面不可见（如预渲染），不记录
      pageEnterTime = null;
      logDebug('Page not visible. Enter time not recorded.');
    }
  };

  /**
   * 初始化：注册事件监听
   */
  const init = () => {
    if (initialized) return;
    initialized = true;

    // 记录初始进入时间
    recordEnterTime();

    // 监听可见性变化
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // 页面隐藏 → 上报停留时长
        reportStay('visibilitychange');
      } else if (document.visibilityState === 'visible') {
        // 页面重新可见 → 记录新的进入时间
        recordEnterTime();
      }
    });

    // 页面卸载
    window.addEventListener('pagehide', () => {
      reportStay('pagehide');
    });

    // 兜底：beforeunload
    window.addEventListener('beforeunload', () => {
      reportStay('beforeunload');
    });

    logDebug('Page stay observer initialized.');
  };

  /**
   * 上报当前页面停留时长并重置计时。
   * 由 core.ts 的 pv() 方法在 SPA 路由切换时调用。
   */
  const reportCurrent = (): void => {
    reportStay('spa-route-change');
    // 重置后，pv() 调用后会由 recordEnterTime 重新记录（如果页面可见）
    // 但 pv() 调用时页面可能已经是可见状态，所以这里直接记录
    recordEnterTime();
  };

  init();

  return { reportCurrent };
}
